// What the Arsenal and Strike bars sell (GDD §7, §7.1, §14). A tile or hotkey buys at once: the sim's doctrines decide
// where it lands, so each item carries its doctrine copy for the tile and its quip for the tooltip. Items are built
// once from content, so identity comparison is item equality and nothing allocates per update.

import { STRIKE_KEYS, STRIKES, UNIT_KEYS, UNITS, type StrikeEffect, type StrikeKey, type UnitKey } from '@taxwar/content'
import {
  DEPLOY_DOCTRINE_COPY,
  DEPLOY_REQUIREMENT_COPY,
  LAUNCH_DOCTRINE_COPY,
  NATIONWIDE_STRIKE_COPY,
  STRIKE_HOTKEYS,
  STRIKE_PRESENTATION,
  STRIKE_QUIPS,
  STRIKE_TARGETING_COPY,
  UNIT_HOTKEYS,
  UNIT_PRESENTATION,
  UNIT_QUIPS,
  type DoctrineCopy,
  type Hotkey,
  type ItemPresentation,
  type RequirementCopy,
} from './catalog'
import { HudError } from './errors'

interface ItemBase {
  readonly presentation: ItemPresentation
  readonly cost: number
  readonly hotkey: Hotkey
  readonly doctrine: DoctrineCopy
  readonly quip: string
  /** What must stand before it can be bought (units: a deploy site); null when nothing does (strikes). */
  readonly requirement: RequirementCopy | null
}

export interface UnitItem extends ItemBase {
  readonly kind: 'unit'
  readonly key: UnitKey
}

export interface StrikeItem extends ItemBase {
  readonly kind: 'strike'
  readonly key: StrikeKey
}

export type BarItem = UnitItem | StrikeItem

type EffectOf<K extends StrikeEffect['kind']> = Extract<StrikeEffect, { readonly kind: K }>

/** How each strike effect kind is placed, in words. */
const STRIKE_DOCTRINE: { readonly [K in StrikeEffect['kind']]: (effect: EffectOf<K>) => DoctrineCopy } = {
  missile: (effect) => {
    const targeting = STRIKE_TARGETING_COPY[effect.targeting]
    return { tag: targeting.tag, rule: `${targeting.rule}; ${LAUNCH_DOCTRINE_COPY[effect.launchFrom]}` }
  },
  leaflets: (effect) => STRIKE_TARGETING_COPY[effect.targeting],
  sanctions: () => NATIONWIDE_STRIKE_COPY,
}

function strikeDoctrine(key: StrikeKey): DoctrineCopy {
  const effect = STRIKES[key].effect
  return (STRIKE_DOCTRINE[effect.kind] as (effect: StrikeEffect) => DoctrineCopy)(effect)
}

export const UNIT_ITEMS: readonly UnitItem[] = UNIT_KEYS.map((key): UnitItem => ({
  kind: 'unit',
  key,
  presentation: UNIT_PRESENTATION[key],
  cost: UNITS[key].cost,
  hotkey: UNIT_HOTKEYS[key],
  doctrine: DEPLOY_DOCTRINE_COPY[UNITS[key].doctrine],
  quip: UNIT_QUIPS[key],
  requirement: DEPLOY_REQUIREMENT_COPY[UNITS[key].deployRequires],
}))

export const STRIKE_ITEMS: readonly StrikeItem[] = STRIKE_KEYS.map((key): StrikeItem => ({
  kind: 'strike',
  key,
  presentation: STRIKE_PRESENTATION[key],
  cost: STRIKES[key].cost,
  hotkey: STRIKE_HOTKEYS[key],
  doctrine: strikeDoctrine(key),
  quip: STRIKE_QUIPS[key],
  requirement: null,
}))

function indexByHotkey(items: readonly BarItem[]): ReadonlyMap<string, BarItem> {
  const byCode = new Map<string, BarItem>()
  for (const item of items) {
    if (byCode.has(item.hotkey.code)) throw new HudError(`hotkey ${item.hotkey.code} is bound twice`)
    byCode.set(item.hotkey.code, item)
  }
  return byCode
}

/** Bar items by KeyboardEvent.code. */
export const ITEMS_BY_HOTKEY: ReadonlyMap<string, BarItem> = indexByHotkey([...UNIT_ITEMS, ...STRIKE_ITEMS])
